import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  CirclePlus,
  ListChecks,
  RotateCcw,
  Save,
  Trash2,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
});

const normalizeEntries = (entries) => (entries || []).map((entry, index) => ({
  value: String(entry.value || entry.label || "").trim(),
  label: String(entry.label || entry.value || "").trim(),
  active: entry.active !== false,
  sort_order: (index + 1) * 10,
}));

const entriesSignature = (entries) => JSON.stringify(normalizeEntries(entries));

const slugValue = (label, existingValues) => {
  const cleaned = String(label || "").trim();
  if (!cleaned) return "";
  let value = cleaned;
  let suffix = 2;
  const existing = new Set((existingValues || []).map((item) => String(item || "").toLowerCase()));
  while (existing.has(value.toLowerCase())) {
    value = `${cleaned} ${suffix}`;
    suffix += 1;
  }
  return value;
};

export const IntakeFormOptionsPage = () => {
  const [fieldLabels, setFieldLabels] = useState({});
  const [fieldDescriptions, setFieldDescriptions] = useState({});
  const [protectedValues, setProtectedValues] = useState({});
  const [fields, setFields] = useState({});
  const [selectedField, setSelectedField] = useState("");
  const [draftEntries, setDraftEntries] = useState([]);
  const [savedSignature, setSavedSignature] = useState("");
  const [newLabel, setNewLabel] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);

  const hasUnsavedChanges = entriesSignature(draftEntries) !== savedSignature;

  const selectField = (fieldKey, nextFields = fields) => {
    const entries = normalizeEntries(nextFields[fieldKey] || []);
    setSelectedField(fieldKey);
    setDraftEntries(entries);
    setSavedSignature(entriesSignature(entries));
    setNewLabel("");
  };

  const loadOptions = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/intake-form-options`, authHeaders());
      const nextFields = response.data?.fields || {};
      const nextLabels = response.data?.field_labels || {};
      setFields(nextFields);
      setFieldLabels(nextLabels);
      setFieldDescriptions(response.data?.field_descriptions || {});
      setProtectedValues(response.data?.protected_active_values || {});
      const firstField = selectedField || Object.keys(nextLabels)[0] || "";
      selectField(firstField, nextFields);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to load intake form dropdowns");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handler = (event) => {
      if (!hasUnsavedChanges) return undefined;
      event.preventDefault();
      event.returnValue = "";
      return "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [hasUnsavedChanges]);

  const fieldChoices = useMemo(
    () => Object.entries(fieldLabels).sort((a, b) => a[1].localeCompare(b[1])),
    [fieldLabels],
  );

  const chooseField = (fieldKey) => {
    if (fieldKey === selectedField) return;
    if (hasUnsavedChanges && !window.confirm("Discard unsaved dropdown changes and switch fields?")) return;
    selectField(fieldKey);
  };

  const updateLabel = (index, label) => {
    setDraftEntries((previous) => previous.map((entry, entryIndex) => (
      entryIndex === index ? { ...entry, label } : entry
    )));
  };

  const moveEntry = (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= draftEntries.length) return;
    setDraftEntries((previous) => {
      const next = [...previous];
      [next[index], next[target]] = [next[target], next[index]];
      return normalizeEntries(next);
    });
  };

  const toggleEntry = (index) => {
    setDraftEntries((previous) => previous.map((entry, entryIndex) => (
      entryIndex === index ? { ...entry, active: !entry.active } : entry
    )));
  };

  const addEntry = () => {
    const label = newLabel.trim();
    if (!label) return;

    const value = slugValue(label, draftEntries.map((entry) => entry.value));
    setDraftEntries((previous) => normalizeEntries([
      ...previous,
      { value, label, active: true },
    ]));
    setNewLabel("");
  };

  const saveField = async () => {
    if (!selectedField) return;
    const emptyLabel = draftEntries.find((entry) => !entry.label.trim());
    if (emptyLabel) {
      toast.error("Every option needs a display label");
      return;
    }

    setSaving(true);
    try {
      const entries = normalizeEntries(draftEntries);
      const response = await axios.put(
        `${API}/admin/intake-form-options/${selectedField}`,
        { entries },
        authHeaders(),
      );
      const savedEntries = normalizeEntries(response.data?.entries || entries);
      setDraftEntries(savedEntries);
      setSavedSignature(entriesSignature(savedEntries));
      setFields((previous) => ({ ...previous, [selectedField]: savedEntries }));
      toast.success(`${fieldLabels[selectedField] || selectedField} dropdown saved`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save dropdown options");
    } finally {
      setSaving(false);
    }
  };

  const resetField = async () => {
    if (!selectedField) return;
    const label = fieldLabels[selectedField] || selectedField;
    if (!window.confirm(`Reset ${label} to InkSight defaults? This replaces this dropdown list, but historical requests keep their saved values.`)) return;

    setResetting(true);
    try {
      const response = await axios.post(
        `${API}/admin/intake-form-options/${selectedField}/reset`,
        {},
        authHeaders(),
      );
      const resetEntries = normalizeEntries(response.data?.entries || []);
      setDraftEntries(resetEntries);
      setSavedSignature(entriesSignature(resetEntries));
      setFields((previous) => ({ ...previous, [selectedField]: resetEntries }));
      toast.success(response.data?.message || `${label} reset to defaults`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reset dropdown options");
    } finally {
      setResetting(false);
    }
  };

  if (loading) {
    return <div className="text-muted-foreground animate-pulse">Loading dropdown management...</div>;
  }

  const activeCount = draftEntries.filter((entry) => entry.active).length;
  const inactiveCount = draftEntries.length - activeCount;
  const selectedLabel = fieldLabels[selectedField] || selectedField;
  const selectedDescription = fieldDescriptions[selectedField] || "Manage the options shown in this dropdown.";
  const protectedForField = new Set((protectedValues[selectedField] || []).map((value) => String(value || "").toLowerCase()));

  return (
    <div className="animate-fade-in space-y-6">
      <div>
        <h1 className="text-3xl font-bold flex items-center gap-3">
          <ListChecks size={30} /> Dropdown Management
        </h1>
        <p className="text-muted-foreground mt-1">
          Manage the selectable values shown on New Design Request and Sales Ops review forms. Changes apply to future selections without rewriting historical records.
        </p>
      </div>

      <div className="grid gap-4 lg:grid-cols-[320px_1fr]">
        <div className="bg-card border border-border rounded-xl p-5 space-y-4 h-fit">
          <div>
            <label className="block text-sm font-medium mb-2">Dropdown Field</label>
            <select
              value={selectedField}
              onChange={(event) => chooseField(event.target.value)}
              className="w-full px-4 py-2.5 bg-background border border-border rounded-lg"
            >
              {fieldChoices.map(([key, label]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </select>
          </div>

          <div className="rounded-lg border border-border bg-background/50 p-4 text-sm space-y-2">
            <div className="flex justify-between gap-3">
              <span className="text-muted-foreground">Active</span>
              <strong>{activeCount}</strong>
            </div>
            <div className="flex justify-between gap-3">
              <span className="text-muted-foreground">Inactive</span>
              <strong>{inactiveCount}</strong>
            </div>
            <div className="flex justify-between gap-3">
              <span className="text-muted-foreground">Total</span>
              <strong>{draftEntries.length}</strong>
            </div>
          </div>

          {hasUnsavedChanges && (
            <div className="flex items-start gap-2 rounded-lg border border-amber-700/60 bg-amber-950/40 p-3 text-sm text-amber-100">
              <AlertTriangle size={16} className="mt-0.5 shrink-0" />
              <span>You have unsaved dropdown changes.</span>
            </div>
          )}
        </div>

        <div className="bg-card border border-border rounded-xl overflow-hidden">
          <div className="p-5 border-b border-border flex flex-wrap items-start justify-between gap-4">
            <div>
              <h2 className="text-xl font-semibold">{selectedLabel}</h2>
              <p className="text-sm text-muted-foreground mt-1 max-w-3xl">{selectedDescription}</p>
              <p className="text-xs text-muted-foreground mt-2">
                Stable values are preserved for history and integrations. Rename the display label when possible; disable unavailable choices instead of deleting them.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={resetField}
                disabled={resetting || saving}
                className="flex items-center gap-2 px-4 py-2.5 bg-secondary rounded-lg font-medium disabled:opacity-50"
              >
                <RotateCcw size={17} /> {resetting ? "Resetting..." : "Reset to Defaults"}
              </button>
              <button
                type="button"
                onClick={saveField}
                disabled={saving || !hasUnsavedChanges}
                className="flex items-center gap-2 px-4 py-2.5 bg-primary text-primary-foreground rounded-lg font-medium disabled:opacity-50"
              >
                <Save size={17} /> {saving ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </div>

          <div className="divide-y divide-border">
            {draftEntries.map((entry, index) => {
              const protectedActive = protectedForField.has(entry.value.toLowerCase());
              return (
                <div key={entry.value} className={`p-4 grid grid-cols-1 xl:grid-cols-[80px_minmax(260px,1fr)_minmax(220px,0.8fr)_140px] gap-4 items-center ${entry.active ? "" : "opacity-60"}`}>
                  <div className="flex gap-1">
                    <button type="button" onClick={() => moveEntry(index, -1)} disabled={index === 0} className="p-2 bg-secondary rounded-md disabled:opacity-30" title="Move up"><ArrowUp size={15} /></button>
                    <button type="button" onClick={() => moveEntry(index, 1)} disabled={index === draftEntries.length - 1} className="p-2 bg-secondary rounded-md disabled:opacity-30" title="Move down"><ArrowDown size={15} /></button>
                  </div>

                  <div>
                    <label className="block text-xs text-muted-foreground mb-1">Display label</label>
                    <input
                      type="text"
                      value={entry.label}
                      onChange={(event) => updateLabel(index, event.target.value)}
                      className="w-full px-3 py-2 bg-background border border-border rounded-lg"
                      aria-label={`Display label for ${entry.value}`}
                    />
                  </div>

                  <div>
                    <p className="text-xs text-muted-foreground">Stable value</p>
                    <code className="text-xs break-all">{entry.value}</code>
                    {protectedActive && <p className="text-xs text-amber-300 mt-1">Required active option</p>}
                  </div>

                  <button
                    type="button"
                    onClick={() => toggleEntry(index)}
                    disabled={protectedActive && entry.active}
                    className={`flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium disabled:opacity-50 ${entry.active ? "bg-red-950/30 text-red-400" : "bg-emerald-950/30 text-emerald-400"}`}
                  >
                    {entry.active ? <><Trash2 size={15} /> Disable</> : <><RotateCcw size={15} /> Restore</>}
                  </button>
                </div>
              );
            })}
          </div>

          <div className="p-5 border-t border-border bg-secondary/20">
            <div className="flex flex-col md:flex-row gap-3">
              <input
                type="text"
                value={newLabel}
                onChange={(event) => setNewLabel(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    addEntry();
                  }
                }}
                placeholder={`New ${selectedLabel} option`}
                className="flex-1 px-4 py-2.5 bg-background border border-border rounded-lg"
              />
              <button type="button" onClick={addEntry} className="flex items-center justify-center gap-2 px-4 py-2.5 bg-secondary rounded-lg font-medium">
                <CirclePlus size={17} /> Add Option
              </button>
            </div>
            <p className="text-xs text-muted-foreground mt-3">
              New options are added as active. Save changes before leaving this page. To temporarily remove an option from the form, use Disable.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};